import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { SimpleToolbar } from "@/components/layout/Toolbar";
import { useGlobalProvider } from "@/context/GlobalContext";
import { useMyTasks } from "@/hooks/useMyTasks";
import { useNotifications } from "@/context/NotificationsContext";
import { TaskStatusBadge } from "@/components/tasks/TaskStatusBadge";
import { NotificationBellButton } from "@/components/notifications/NotificationBell";
import { DataCompletenessButton } from "@/components/dataCompleteness/DataCompletenessButton";
import { formatTaskTimestamps } from "@/utils/formatDate";
import { getInitials, getColorForName } from "@/utils/noteUtils";
import type { DealTask, TaskStatus } from "@/types/dealTask";
import "@/assets/css/Text-General.css";
import "@/assets/css/tabs.css";
import "@/assets/css/summary.css";

const AVATAR_SIZE = 22;
const COMPLETED_TASK_CARD_BORDER = "#12B76A";
const COMPLETED_TASK_CARD_BG = "rgba(18, 183, 106, 0.08)";
const TASK_CARD_BG = "#fff";
const TASK_CARD_BORDER = "rgba(0, 0, 0, 0.12)";
const TASK_DATE_COLOR = "#b8c0c8";

type MyTasksTab = TaskStatus;

function taskCardPaperSx(isCompleted: boolean) {
  const border = isCompleted ? COMPLETED_TASK_CARD_BORDER : TASK_CARD_BORDER;
  const bg = isCompleted ? COMPLETED_TASK_CARD_BG : TASK_CARD_BG;

  return {
    p: 1.25,
    cursor: "pointer",
    transition: "box-shadow 0.15s ease",
    backgroundColor: `${bg} !important`,
    borderColor: `${border} !important`,
    "&:hover": {
      backgroundColor: `${bg} !important`,
      boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.08) !important",
    },
  };
}

function InitialsAvatar({ name, title }: { name: string | null; title?: string }) {
  const displayName = name ?? "?";
  const initials = getInitials(displayName);
  const bgColor = getColorForName(displayName);

  return (
    <Tooltip title={title ?? displayName} arrow>
      <Box
        sx={{
          width: AVATAR_SIZE,
          height: AVATAR_SIZE,
          borderRadius: "50%",
          backgroundColor: bgColor,
          color: "#FFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          fontSize: "0.625rem",
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        {initials}
      </Box>
    </Tooltip>
  );
}

export default function MyTasks() {
  const navigate = useNavigate();
  const { user, userLoading } = useGlobalProvider();
  const { markReadByEntity } = useNotifications();
  const [activeTab, setActiveTab] = useState<MyTasksTab>("open");

  const userId = user?.tenfiftyid as number | undefined;
  const { tasks, loading, error } = useMyTasks(userId, activeTab);

  const handleRowClick = async (task: DealTask) => {
    if (userId) {
      await markReadByEntity({ entity_type: "deal_task", entity_id: task.id });
    }
    navigate(`/deals/${task.deal_id}?tasks=1`);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        padding: "0px",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          "& .toolbar": {
            minHeight: 56,
            boxSizing: "border-box",
          },
        }}
      >
        <SimpleToolbar>
          <Box sx={{ display: "flex", alignItems: "center", gap: "15px", minHeight: 32 }} />
          <Box
            data-position="right"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              ml: -0.5,
              "& .MuiIconButton-root": {
                p: 0.6,
              },
              "& .MuiIconButton-root .MuiSvgIcon-root": {
                fontSize: 22,
              },
            }}
          >
            <DataCompletenessButton />
            <NotificationBellButton />
          </Box>
        </SimpleToolbar>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          minHeight: 0,
          padding: 0,
        }}
      >
        <Paper elevation={0} className="summary-card">
          <Tabs
            value={activeTab}
            onChange={(_, value: MyTasksTab) => setActiveTab(value)}
            variant="standard"
            TabIndicatorProps={{ className: "tf-tabs__indicator" }}
            className="summary-tabs"
          >
            <Tab label="Open" value="open" className="tf-tab" />
            <Tab label="Completed" value="completed" className="tf-tab" />
          </Tabs>

          <div className="summary-tab-panel">
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {(userLoading || loading) && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {!userLoading && !userId && (
              <Typography color="text.secondary" variant="body2">
                Unable to load your user profile.
              </Typography>
            )}

            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}

            {!loading && !userLoading && userId && tasks.length === 0 && (
              <Typography color="text.secondary" variant="body2">
                No {activeTab} tasks assigned to you.
              </Typography>
            )}

            {!loading &&
              tasks.map((task) => {
                const isCompleted = task.status === "completed";

                return (
                  <Paper
                    key={task.id}
                    elevation={0}
                    className="summary-card"
                    onClick={() => void handleRowClick(task)}
                    sx={taskCardPaperSx(isCompleted)}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 0.75,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          flexShrink: 0,
                          pt: 0.125,
                        }}
                      >
                        <InitialsAvatar
                          name={task.created_by_name}
                          title={`Assigned by ${task.created_by_name ?? "Unknown"}`}
                        />
                        <ChevronRightIcon
                          sx={{ fontSize: 14, color: "#adadad", flexShrink: 0 }}
                        />
                        <InitialsAvatar
                          name={task.assigned_to_name}
                          title={`Assigned to ${task.assigned_to_name ?? "Unassigned"}`}
                        />
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: "0.8125rem",
                            fontWeight: isCompleted ? 400 : 500,
                            color: isCompleted ? "text.secondary" : "#182737",
                            lineHeight: 1.35,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {task.description}
                        </Typography>
                      </Box>

                      <Typography
                        noWrap
                        sx={{
                          flexShrink: 0,
                          maxWidth: "40%",
                          fontSize: "0.6875rem",
                          color: "text.secondary",
                          textAlign: "right",
                          pt: 0.125,
                        }}
                      >
                        {task.deal_name ?? `Deal #${task.deal_id}`}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.5,
                        minHeight: 22,
                        flexWrap: "wrap",
                      }}
                    >
                      <TaskStatusBadge status={task.status} />
                      <Typography
                        component="span"
                        noWrap={!isCompleted}
                        sx={{
                          color: `${TASK_DATE_COLOR} !important`,
                          fontSize: "0.625rem",
                          lineHeight: 1.2,
                        }}
                      >
                        {formatTaskTimestamps(task)}
                      </Typography>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </div>
        </Paper>
      </Box>
    </Box>
  );
}
